import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import { api } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { Building2, User, Mail, Phone, Shield } from 'lucide-react';

/**
 * Tela para verificar qual síndico/subsíndico do condomínio selecionado.
 * Acesso restrito ao administrador.
 */
const SindicoCondominio = () => {
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioId, isAdministrador } = useCondominio();
  const [loading, setLoading] = useState(false);
  const [dadosCondominio, setDadosCondominio] = useState(null);
  const [erro, setErro] = useState('');

  const condominioId = getCondominioId?.() || condominioSelecionado?.condominio_id;

  useEffect(() => {
    if (!condominioId || !isAdministrador()) return;
    setErro('');
    setLoading(true);
    api.get(`/condominios/${condominioId}`)
      .then((res) => {
        const cond = res.data?.data?.condominio || res.data?.condominio;
        setDadosCondominio(cond);
      })
      .catch((err) => {
        setErro(err.response?.data?.message || 'Erro ao carregar dados do condomínio.');
        setDadosCondominio(null);
      })
      .finally(() => setLoading(false));
  }, [condominioId, isAdministrador]);

  if (!user) return null;

  if (!isAdministrador()) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Acesso negado. Apenas administradores podem acessar esta página.</p>
        </div>
      </div>
    );
  }

  const sindico = dadosCondominio?.sindico;
  const subsindico = dadosCondominio?.subsindico;
  const temSindico = sindico && (sindico.usuario_id != null || sindico.usuario_nome);
  const temSubsindico = subsindico && (subsindico.usuario_id != null || subsindico.usuario_nome);

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Síndico do Condomínio</h1>
        </div>
      </div>

      {!condominioId && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          Selecione um condomínio no menu superior para visualizar o síndico e o subsíndico.
        </div>
      )}

      {loading && <LoadingSpinner />}

      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 mb-4">
          {erro}
        </div>
      )}

      {!loading && !erro && condominioId && (
        <div className="space-y-6">
          <div className="flex items-center gap-2 text-gray-700 mb-4">
            <Building2 className="h-5 w-5" />
            <span className="font-medium">{dadosCondominio?.condominio_nome || condominioSelecionado?.condominio_nome}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Síndico */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-primary-600" />
                Síndico
              </h2>
              {temSindico ? (
                <div className="space-y-3">
                  <p className="font-medium text-gray-900">{sindico.usuario_nome}</p>
                  {sindico.usuario_email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="h-4 w-4" />
                      <a href={`mailto:${sindico.usuario_email}`} className="hover:text-primary-600">{sindico.usuario_email}</a>
                    </div>
                  )}
                  {sindico.usuario_telefone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="h-4 w-4" />
                      <span>{sindico.usuario_telefone}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Nenhum síndico cadastrado para este condomínio.</p>
              )}
            </div>

            {/* Subsíndico */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-primary-600" />
                Subsíndico
              </h2>
              {temSubsindico ? (
                <div className="space-y-3">
                  <p className="font-medium text-gray-900">{subsindico.usuario_nome}</p>
                  {subsindico.usuario_email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="h-4 w-4" />
                      <a href={`mailto:${subsindico.usuario_email}`} className="hover:text-primary-600">{subsindico.usuario_email}</a>
                    </div>
                  )}
                  {subsindico.usuario_telefone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="h-4 w-4" />
                      <span>{subsindico.usuario_telefone}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Nenhum subsíndico cadastrado para este condomínio.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && !erro && !condominioId && (
        <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-600">
          Selecione um condomínio no menu superior para visualizar o síndico e o subsíndico.
        </div>
      )}
    </div>
  );
};

export default SindicoCondominio;
